import React, {
    Fragment,
    useRef,
    useState,
    useEffect,
    useContext
} from "react";

import { ImageWithGrid } from "../Image";

import ElementContext from "../ElementContext";
import MediaTextContext from "../../content-elements/MediaText/MediaTextContext";

import { generateClassString as gcs } from "../../lib/helper/generateClassString";
import { useIsDesktop, useIsMobile } from "../../lib/hooks/mediaQueries";
import GeneralPageContext from "../../templates/GeneralPageContext";
import { useMediaQuery } from "react-responsive";

import * as styles from "./media.module.less";
import useVideo from "../../lib/hooks/useVideo";
import t from "../../lib/translate";
import { createStructuredData } from "../../lib/helper/createStructuredData";

function Media({
    children,
    mediaElement = null,
    className,
    thumbnailsOnly,
    videoControls,
    playButtonClass,
    gridDisable = false,
    track = <track />,
    withGallery,
    gridSize,
    showDescription,

    // This value is only set when <Media> is used within <MediaTextAdvanced>
    eventKey,

    ...other
}) {
    const {
        ref: videoRef,
        videoStarted,
        setVideoStarted,
        isPlaying,
        eventHandler
    } = useVideo();
    // const videoRef = useRef();
    // const [isPlaying, setisPlaying] = useState(false);
    // const [videoStarted, setisPlaying] = useState(false);

    const customPreview = mediaElement?.customPreview;
    const { column } = useContext(ElementContext);

    // This value is only set when <Media> is used within <MediaText> or <MediaSlider>
    const { activeElement } = useContext(MediaTextContext);

    const isMobile = useIsMobile();
    const isDesktop = useIsDesktop();
    const isMax1600 = useMediaQuery({ query: "(max-width: 1600px)" });

    const {
        layout: { showAnchorNavigation }
    } = useContext(GeneralPageContext);

    useEffect(() => {
        if (videoRef.current && activeElement !== eventKey) {
            videoRef.current.pause();
            setVideoStarted(false);
        }
    }, [activeElement]);

    const description =
        mediaElement?.mediaDescription || mediaElement?.media?.alt || "";

    const withDescription =
        showDescription ?? !mediaElement?.hideImageDescription;

    if (mediaElement?.media?.isImage) {
        return (
            <ImageWithGrid
                imageClass={className}
                image={mediaElement.media}
                gridDisable={gridDisable}
                gridSize={gridSize}
                mediaDescription={withDescription ? description : false}
                {...other}
            />
        );
    }

    if (!customPreview || typeof customPreview.forVideo !== "boolean") {
        return null;
    }

    if (
        customPreview.forVideo &&
        !thumbnailsOnly &&
        !mediaElement.media.video?.mp4Url
    ) {
        return null;
    }

    function playVideo(event) {
        // in case we are embedded in another clickable area the event should be handled here only
        event.stopPropagation();
        videoRef.current.play();
    }

    return (
        <Fragment>
            {!customPreview.forVideo ? (
                <ImageWithGrid
                    alt={customPreview.alt}
                    title={customPreview.title}
                    image={
                        customPreview.mediaNode || { url: customPreview.url }
                    }
                    imageClass={className}
                    {...other}
                    gridSize={
                        (column === 1 && isMobile) ||
                        (column === 2 && isDesktop) ||
                        (showAnchorNavigation && column === 2 && isMax1600)
                            ? "s"
                            : "m"
                    }
                    gridDisable={gridDisable}
                    mediaDescription={showDescription ? description : false}
                >
                    {children}
                </ImageWithGrid>
            ) : (
                <div
                    className={gcs(styles.videoWrapper, [
                        !gridDisable,
                        styles.moveLeft
                    ])}
                >
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    {!thumbnailsOnly && (
                        <div
                            className={gcs(
                                styles.innerVideoWrap,
                                [gridDisable, styles.hideGrid],
                                (column === 1 && isMobile) ||
                                    (column === 2 && isDesktop) ||
                                    (showAnchorNavigation &&
                                        column === 2 &&
                                        isMax1600)
                                    ? styles.smallGrid
                                    : styles.bigGrid
                            )}
                        >
                            {createStructuredData("VideoObject", {
                                name: mediaElement?.media.title,
                                thumbnailUrl: mediaElement?.media.video.thumbnailUrl,
                                uploadDate: mediaElement?.media.createdAt,
                            })}
                            <video
                                ref={videoRef}
                                src={mediaElement?.media.video.mp4Url}
                                className={className}
                                controls={videoControls}
                                preload="none"
                                //Fixme: Known issue. The event handler onPause
                                //       gets triggered whenever the user drags
                                //       the progressbar slider of a video
                                {...eventHandler}
                                hidden={!videoStarted}
                                {...other}
                            >
                                {track}
                                {children}
                            </video>
                        </div>
                    )}

                    {!videoStarted && (
                        <div
                            className={gcs(
                                styles.previewWrapper
                                //     [
                                //     videoStarted,
                                //     styles.hidePreview
                                // ]
                            )}
                        >
                            <ImageWithGrid
                                gridSize={
                                    (column === 1 && isMobile) ||
                                    (column === 2 && isDesktop) ||
                                    (showAnchorNavigation &&
                                        column === 2 &&
                                        isMax1600)
                                        ? "s"
                                        : "m"
                                }
                                gridDisable={gridDisable}
                                gridWrapperClass={styles.imageWrapper}
                                className={gcs(className, styles.previewImage, [
                                    !gridDisable,
                                    styles.marginTop
                                ])}
                                image={
                                    customPreview.mediaNode || {
                                        url: customPreview.url
                                    }
                                }
                                imageClass={styles.previewImage}
                                alt={customPreview.alt}
                                mediaDescription={
                                    showDescription ? description : false
                                }
                                {...other}
                            />

                            {/* Not render play button when <Media> element is used with media gallery */}
                            {!withGallery && (
                                <button
                                    className={gcs(styles.playButton, [
                                        !gridDisable,
                                        styles.playButtonOffset
                                    ])}
                                    {...(!thumbnailsOnly && {
                                        onClick: playVideo
                                    })}
                                    aria-label={t(
                                        "media-text-advanced.play.video"
                                    )}
                                />
                            )}
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
}

export { Media };
